import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Eye, EyeOff } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Logo } from '../components/Logo'
import { Button, Input, Text } from '../components/ui'
import { authClient } from '../lib/auth-client'

const resetPasswordSearchSchema = z.object({
  token: z.string().optional(),
  error: z.string().optional(),
})

const resetPasswordSchema = z
  .object({
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Confirm your new password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

type ResetPasswordForm = z.infer<typeof resetPasswordSchema>

export const Route = createFileRoute('/reset-password')({
  validateSearch: resetPasswordSearchSchema,
  component: ResetPasswordPage,
})

function ResetPasswordPage() {
  const navigate = useNavigate()
  const { token, error } = Route.useSearch()
  const [showPassword, setShowPassword] = useState(false)
  const [globalError, setGlobalError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  })

  const isInvalidToken = error === 'INVALID_TOKEN' || !token

  async function onSubmit(data: ResetPasswordForm) {
    if (!token) return

    setGlobalError(null)

    const { error: resetError } = await authClient.resetPassword({
      newPassword: data.newPassword,
      token,
    })

    if (resetError) {
      setGlobalError(resetError.message ?? 'Could not reset your password. Please try again.')
      return
    }

    navigate({ to: '/login' })
  }

  if (isInvalidToken) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-md text-center">
          <Logo className="mx-auto mb-6" />
          <Text variant="h1" className="mb-3">
            Link expired
          </Text>
          <Text variant="body-sm" className="mb-8 text-foreground/60">
            This password reset link is invalid or has expired. Request a new link to continue.
          </Text>
          <Button
            fullWidth
            size="lg"
            onClick={() => navigate({ to: '/forgot-password' })}
          >
            Request new link
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-[480px]">
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo className="mb-6" />
          <Text variant="h1" className="mb-2">
            Choose a new password
          </Text>
          <Text variant="body-sm" className="text-foreground/60">
            Enter and confirm your new password below.
          </Text>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label="New password"
            type={showPassword ? 'text' : 'password'}
            placeholder="At least 8 characters"
            autoComplete="new-password"
            error={errors.newPassword?.message}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                className="text-foreground/50 hover:text-foreground"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            }
            {...register('newPassword')}
          />

          <Input
            label="Confirm new password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Re-enter your password"
            autoComplete="new-password"
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />

          {globalError && (
            <Text variant="body-sm" className="text-red-600">
              {globalError}
            </Text>
          )}

          <Button type="submit" fullWidth size="lg" loading={isSubmitting}>
            Reset password
          </Button>
        </form>

        <Text variant="body-sm" className="mt-6 text-center text-foreground/60">
          <Link to="/login" className="font-medium text-foreground underline">
            Back to login
          </Link>
        </Text>
      </div>
    </div>
  )
}
